import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useBlocker } from 'react-router-dom';

type ContextType = {
  blockerState: NavigationBlockerState;
  shouldBeBlocked: boolean;
  setShouldBeBlocked: React.Dispatch<React.SetStateAction<boolean>>;
  reset: () => void;
  proceed: () => void;
};

type NavigationBlockerState = 'unblocked' | 'blocked' | 'proceeding';

const NavigationBlockerContext = createContext({
  blockerState: 'unblocked',
  shouldBeBlocked: false,
  setShouldBeBlocked: () => {},
  reset: () => {},
  proceed: () => {},
} as ContextType);

type ProviderProps = {
  children: ReactNode | JSX.Element;
};

const NavigationBlockerProvider = ({ children }: ProviderProps) => {
  const [blockerState, setBlockerState] =
    useState<NavigationBlockerState>('unblocked');
  const [shouldBeBlocked, setShouldBeBlocked] = useState(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const condition =
      shouldBeBlocked && currentLocation.pathname !== nextLocation.pathname;
    setBlockerState(blocker.state);
    return condition;
  });

  useEffect(() => {
    setBlockerState(blocker.state);
  }, [blocker.state]);


  const reset = useCallback(() => {
    if (blocker.reset) blocker.reset();
    setBlockerState('unblocked');
    setShouldBeBlocked(true);
  }, [blocker]);

  const proceed = useCallback(() => {
    if (blockerState) {
      setBlockerState('unblocked');
      setShouldBeBlocked(false);
    }
    if (blocker.proceed) blocker.proceed();
  }, [blocker, blockerState]);

  const contextValue = useMemo(() => {
    return {
      blockerState,
      shouldBeBlocked,
      setShouldBeBlocked,
      reset,
      proceed,
    };
  }, [blockerState, proceed, reset, shouldBeBlocked]);
  return (
    <NavigationBlockerContext.Provider value={contextValue}>
      {children}
    </NavigationBlockerContext.Provider>
  );
};

const useNavigationBlocker = () => {
  return useContext(NavigationBlockerContext);
};

// eslint-disable-next-line react-refresh/only-export-components
export { useNavigationBlocker, NavigationBlockerProvider };
